import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShoppingCart, Save, Euro, ListChecks } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { MeseGiorniEsplosi } from "../components/MeseGiorniEsplosi";
import { toast } from "sonner";

const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const eur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProgrammaSpesa() {
  const { selectedStructure, structures } = useAuth();
  const qc = useQueryClient();
  const today = ymd(new Date());
  const [mese, setMese] = useState(today.slice(0, 7));
  const [openDay, setOpenDay] = useState(null);
  const [items, setItems] = useState("");
  const [importo, setImporto] = useState("");

  const structId = selectedStructure !== "all" ? selectedStructure : ((structures || []).length === 1 ? structures[0].id : null);

  const { data: monthData } = useQuery({
    queryKey: ["spesa-month", structId, mese], enabled: !!structId, staleTime: 0, refetchOnMount: "always",
    queryFn: async () => (await api.get("/spesa/month", { params: { structure_id: structId, mese } })).data,
  });
  const { data: dayData } = useQuery({
    queryKey: ["spesa-day", structId, openDay], enabled: !!structId && !!openDay, staleTime: 0, refetchOnMount: "always",
    queryFn: async () => (await api.get("/spesa/day", { params: { structure_id: structId, data: openDay } })).data,
  });

  useEffect(() => {
    setItems(dayData?.items || "");
    setImporto(dayData?.importo ? String(dayData.importo) : "");
  }, [dayData]);

  const dirty = (items || "").trim() !== (dayData?.items || "").trim() || (String(importo || "") !== (dayData?.importo ? String(dayData.importo) : ""));
  const saveDay = async () => {
    if (!structId) { toast.error("Seleziona una struttura specifica in alto."); return; }
    try {
      await api.post("/spesa/register", { structure_id: structId, data: openDay, items, importo: parseFloat(importo) || 0 });
      qc.invalidateQueries({ queryKey: ["spesa-day"] }); qc.invalidateQueries({ queryKey: ["spesa-month"] });
      toast.success("Spesa del giorno salvata");
    } catch { toast.error("Errore nel salvataggio"); }
  };

  if (!structId) {
    return (
      <div>
        <h1 className="text-2xl font-head font-bold flex items-center gap-2 mb-3"><ShoppingCart size={22} className="text-orange-600" /> Programma Spesa</h1>
        <div className="gr-card p-8 text-center" data-testid="spesa-no-struct">
          <ShoppingCart size={34} className="mx-auto text-orange-400 mb-2" />
          <p className="text-sm text-muted-foreground">Seleziona una <b>struttura</b> dal selettore in alto per programmare la spesa.</p>
        </div>
      </div>
    );
  }

  const dayContent = (
    <div className="space-y-4" data-testid="spesa-day-content">
      <div className="gr-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="font-head font-bold flex items-center gap-2"><ListChecks size={18} className="text-orange-600" /> Spesa da fare</h3>
          <Button size="sm" variant={dirty ? "default" : "outline"} className={dirty ? "" : "text-muted-foreground border-muted"} onClick={saveDay} data-testid="spesa-save-day"><Save size={14} className="mr-1" /> {dirty ? "Salva" : "Salvato"}</Button>
        </div>
        <label className="text-xs font-semibold mb-1 block">Lista della spesa <span className="text-muted-foreground font-normal">(una voce per riga)</span></label>
        <Textarea rows={8} value={items} onChange={(e) => setItems(e.target.value)} placeholder="Es.&#10;Pane 5 kg&#10;Latte 20 L&#10;Verdura mista&#10;Detersivi" data-testid="spesa-items" />
        <div className="mt-3 max-w-[240px]">
          <label className="text-xs font-semibold mb-1 block">Valore di spesa del giorno</label>
          <div className="relative">
            <Euro size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input type="number" min="0" step="0.01" value={importo} onChange={(e) => setImporto(e.target.value)} placeholder="0,00" className="h-9 pl-8" data-testid="spesa-importo" />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><ShoppingCart size={22} className="text-orange-600" /> Programma Spesa</h1>
          <p className="text-sm text-muted-foreground">Programma la spesa da fare giorno per giorno e segna il valore speso.</p>
        </div>
        <div className="rounded-xl border bg-white px-4 py-2 text-right" data-testid="spesa-totale-mese">
          <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Totale mese</div>
          <div className="text-xl font-head font-bold text-orange-700">{eur(monthData?.totale || 0)}</div>
        </div>
      </div>

      <MeseGiorniEsplosi embedded color="#ea7317" allowFuture futureMonths={2} largeCells
        renderDayExtra={(iso) => {
          const d = monthData?.days?.[iso];
          const its = (d?.items || "").trim();
          const imp = d?.importo || 0;
          return (
            <div className="mt-2" data-testid={`spesa-day-preview-${iso}`}>
              {its ? (
                <div className="text-[10px] leading-tight text-slate-600 line-clamp-3 whitespace-pre-line">{its}</div>
              ) : <div className="text-[10px] text-muted-foreground italic">Nessuna spesa</div>}
              {imp > 0 && <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`spesa-day-importo-${iso}`}><Euro size={11} /> {eur(imp)}</div>}
            </div>
          );
        }}
        mese={mese} onMese={(m) => { setMese(m); setOpenDay(null); }} openDay={openDay} onOpenDay={setOpenDay}
        counts={monthData?.counts || {}} countLabel="voci">
        {dayContent}
      </MeseGiorniEsplosi>
    </div>
  );
}
